import { BybSection } from "@Components/common/byb-ds";
import { BybButtonVR } from "@Components/game/common";
import Icon from "@Components/generic/icon";
import { faCalendar } from "@fortawesome/pro-regular-svg-icons";
import useI18n from "@i18n";
import { Box, Paper, Stack, Typography, useTheme } from "@mui/material";
import { isNumber } from "@mui/x-data-grid/internals";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

// Extend dayjs with relativeTime plugin
dayjs.extend(relativeTime);

interface IPlaylistResult {
  score: number | null;
  date: string;
  loading: boolean;
  onViewAllResults?: () => void;
  hasResults?: boolean;
}

const PlaylistResult = ({ score, date, loading, onViewAllResults, hasResults = false }: IPlaylistResult) => {
  const theme = useTheme();
  const hasValidScore = !loading && isNumber(score);
  const { i18n } = useI18n();

  // Format the BYB IQ score to display with appropriate precision
  const formatBybIqScore = (score: number | null) => {
    if (score === null) return "0";
    return score.toFixed(0);
  };

  return (
    <Paper data-shape="squircle" sx={{ display: "flex", flexDirection: "column" }}>
      <BybSection
        title={i18n("playlist.results.header")}
        tight
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          backgroundColor: theme.palette.dsSurface.v100,
          paddingBottom: theme.spacing(2),
        }}
      >
        <Stack
          direction="row"
          spacing={3}
          padding={theme.spacing(2, 2.5)}
          boxSizing={"border-box"}
          width={"100%"}
          justifyContent={hasValidScore ? "space-between" : "center"}
          alignItems="center"
        >
          <Typography
            variant={hasValidScore ? "text1xl700" : "textLgItalic"}
            color="dsBase.v200"
            sx={hasValidScore ? undefined : { flexGrow: 1, textAlign: "center" }}
          >
            {i18n(hasValidScore ? "playlist.best.result" : "playlist.best.result.no.data", { score: score || 0 })}
          </Typography>

          {/* Best BYB IQ display */}
          {hasValidScore && (
            <Typography variant="text1xl700" color="dsBase.v200">
              {i18n("playlists.reward.best.iq", {
                iq: formatBybIqScore(score),
              })}
            </Typography>
          )}

          {hasValidScore && (
            <Box sx={{ display: "flex", alignItems: "center", gap: theme.spacing(1) }}>
              <Icon icon={faCalendar} size="sm" color={theme.palette.dsBase.v200} />
              <Typography variant="textBase600" color="dsBase.v200">
                {date ? dayjs(date).fromNow() : i18n("playlists.reward.no.date.available")}
              </Typography>
            </Box>
          )}
        </Stack>

        {onViewAllResults && (
          <Stack direction="row" justifyContent="flex" padding={theme.spacing(0, 2.5)}>
            <BybButtonVR variant="outlined" sx={{ width: "100%" }} color="secondary" size="small" onClick={onViewAllResults} disabled={!hasResults}>
              {i18n("playlist.results.see-all")}
            </BybButtonVR>
          </Stack>
        )}
      </BybSection>
    </Paper>
  );
};

export default PlaylistResult;
